import { Subscription } from "rxjs";
import { StatisticsService } from "app/services/statistics.service";
import { Component, OnInit, OnDestroy, ViewChild, ChangeDetectionStrategy } from "@angular/core";
import { IAlertStatistics } from "app/models/statistics.model";
import { AlertsByLocationComponent } from "../../alerts-by-location/alerts-by-location.component";
import { AlertsPerAlertTypeComponent } from "../../alerts-per-alert-type/alerts-per-alert-type.component";
import { AlertsPerDayComponent } from "../../alerts-per-day/alerts-per-day.component";
import { AverageAlertDurationsComponent } from "../../average-alert-durations/average-alert-durations.component";
import { AlertsPerCareRecipientComponent } from "../../alerts-per-care-recipient/alerts-per-care-recipient.component";
import { AlertsPerHourComponent } from "../../alerts-per-hour/alerts-per-hour.component";
import { TranslateModule } from "@ngx-translate/core";
import { MatProgressBarModule } from '@angular/material/progress-bar';
import {
  StatisticsExportSnapshot,
  StatisticsExportWidget,
  StatisticsKpi,
  StatisticsTabExportSource,
  StatisticsExportProgressCallback,
} from "app/services/statistics-export.models";
import { HelperService } from "app/services/helper.service";

@Component({
    selector: "alerts-tab",
    templateUrl: "./alerts-tab.component.html",
    styleUrls: ["./alerts-tab.component.css"],
    changeDetection: ChangeDetectionStrategy.Default,
    imports: [
    TranslateModule,
    AlertsByLocationComponent,
    AlertsPerAlertTypeComponent,
    AlertsPerDayComponent,
    AlertsPerHourComponent,
    AverageAlertDurationsComponent,
    AlertsPerCareRecipientComponent,
    MatProgressBarModule,
  ],
})
export class AlertsTabComponent implements OnInit, OnDestroy, StatisticsTabExportSource {
  constructor(
    public statistics: StatisticsService,
    private helper: HelperService
  ) {}

  subscription: Subscription;

  alertStatisticsData: IAlertStatistics;
  techDurationAvgs: number[] = [];
  queueTimeAvgs: number[] = [];
  reservedTimeAvgs: number[] = [];
  careTimeAvgs: number[] = [];
  totalAlerts: number = 0;

  @ViewChild(AlertsByLocationComponent) alertsByLocation: AlertsByLocationComponent;
  @ViewChild(AlertsPerAlertTypeComponent) alertsPerAlertType: AlertsPerAlertTypeComponent;
  @ViewChild(AlertsPerDayComponent) alertsPerDay: AlertsPerDayComponent;
  @ViewChild(AlertsPerHourComponent) alertsPerHour: AlertsPerHourComponent;
  @ViewChild(AverageAlertDurationsComponent) averageAlertDurations: AverageAlertDurationsComponent;
  @ViewChild(AlertsPerCareRecipientComponent) alertsPerCareRecipient: AlertsPerCareRecipientComponent;

  ngOnInit() {
    this.totalAlerts = 0;
    this.statistics.initPageCounter();

    this.subscription = this.statistics.alertStatistics$.subscribe((alertStatPage) => {
      if (!alertStatPage) {
        return;
      }

      if (this.statistics.page === 1) {
        this.alertStatisticsData = structuredClone(alertStatPage);
        this.totalAlerts = this.alertStatisticsData.locationAlerts.totalAlerts;
        this.statistics.alertsTabDataLoaded = true;

        this.techDurationAvgs.push(
          alertStatPage.averageDurations.avgTechDurationSeconds * alertStatPage.locationAlerts.totalAlerts
        );
        this.queueTimeAvgs.push(
          alertStatPage.averageDurations.avgQueueTimeSeconds * alertStatPage.locationAlerts.totalAlerts
        );
        this.reservedTimeAvgs.push(
          alertStatPage.averageDurations.avgReservedTimeSeconds * alertStatPage.locationAlerts.totalAlerts
        );
        this.careTimeAvgs.push(
          alertStatPage.averageDurations.avgCareTimeSeconds * alertStatPage.locationAlerts.totalAlerts
        );
      } else {
        this.statistics.alertsTabDataLoaded = true;
        this.totalAlerts = this.alertStatisticsData.locationAlerts.totalAlerts += alertStatPage.locationAlerts.totalAlerts;

        for (const alertLocation of alertStatPage.locationAlerts.alertsByLocations) {
          const existing = this.alertStatisticsData.locationAlerts.alertsByLocations.find(
            (loc) => loc.locationId === alertLocation.locationId
          );
          if (existing) {
            existing.alertCount += alertLocation.alertCount;
          } else {
            this.alertStatisticsData.locationAlerts.alertsByLocations.push({ ...alertLocation });
          }
        }

        alertStatPage.alertsByTypes.forEach((newAlertType) => {
          const existing = this.alertStatisticsData.alertsByTypes.find(
            (a) => a.deviceType === newAlertType.deviceType
          );
          if (!existing) {
            this.alertStatisticsData.alertsByTypes.push({
              ...newAlertType,
              activations: newAlertType.activations.map((a) => ({ ...a })),
            });
          } else {
            newAlertType.activations.forEach((newActivation) => {
              const existingActivation = existing.activations.find(
                (a) => a.group === newActivation.group && a.node === newActivation.node
              );
              if (existingActivation) {
                existingActivation.alertCount += newActivation.alertCount;
              } else {
                existing.activations.push({ ...newActivation });
              }
            });
            existing.totalAlerts += newAlertType.totalAlerts;
          }
        });

        for (let i = 0; i < alertStatPage.alertsByDays.length; i++) {
          const deviceType = alertStatPage.alertsByDays[i].deviceType;

          let dayEntry = this.alertStatisticsData.alertsByDays.find(
            (entry) => entry.deviceType === deviceType
          );
          if (!dayEntry) {
            this.alertStatisticsData.alertsByDays.push({
              deviceType,
              alertsPerDay: [...alertStatPage.alertsByDays[i].alertsPerDay],
            });
          } else {
            for (let d = 0; d < 7; d++) {
              dayEntry.alertsPerDay[d] += alertStatPage.alertsByDays[i].alertsPerDay[d];
            }
          }

          let hourEntry = this.alertStatisticsData.alertsByHours.find(
            (entry) => entry.deviceType === deviceType
          );
          if (!hourEntry) {
            this.alertStatisticsData.alertsByHours.push({
              deviceType,
              alertsPerHour: [...alertStatPage.alertsByHours[i].alertsPerHour],
            });
          } else {
            for (let h = 0; h < 24; h++) {
              hourEntry.alertsPerHour[h] += alertStatPage.alertsByHours[i].alertsPerHour[h];
            }
          }
        }

        for (const newDeviceAlert of alertStatPage.deviceAlerts) {
          const existing = this.alertStatisticsData.deviceAlerts.find(
            (d) => d.deviceId === newDeviceAlert.deviceId
          );
          if (existing) {
            existing.alertCount += newDeviceAlert.alertCount;
          } else {
            this.alertStatisticsData.deviceAlerts.push({ ...newDeviceAlert });
          }
        }

        this.techDurationAvgs.push(
          alertStatPage.averageDurations.avgTechDurationSeconds * alertStatPage.locationAlerts.totalAlerts
        );
        this.queueTimeAvgs.push(
          alertStatPage.averageDurations.avgQueueTimeSeconds * alertStatPage.locationAlerts.totalAlerts
        );
        this.reservedTimeAvgs.push(
          alertStatPage.averageDurations.avgReservedTimeSeconds * alertStatPage.locationAlerts.totalAlerts
        );
        this.careTimeAvgs.push(
          alertStatPage.averageDurations.avgCareTimeSeconds * alertStatPage.locationAlerts.totalAlerts
        );
      }

      const pageTotal = alertStatPage.locationAlerts.totalAlerts;
      if (pageTotal > 0 && pageTotal % this.statistics.pageSize === 0) {
        this.statistics.fetchAlertStatistics$.next();
      } else {
        this.finalizeAlertStatisticsMerge();
      }
    },
      (err) => {
        console.log(err);
        this.statistics.alertsTabDataLoaded = true;
        this.finalizeAlertStatisticsMerge();
      },
      () => {
        this.statistics.alertsTabDataLoaded = true;
      });
    this.statistics.fetchAlertStatistics$.next();
  }

  private finalizeAlertStatisticsMerge(): void {
    if (!this.alertStatisticsData) {
      this.statistics.alertsTabPagingComplete = true;
      this.statistics.initPageCounter();
      return;
    }

    const total = this.alertStatisticsData.locationAlerts.totalAlerts;
    if (total > 0) {
      this.alertStatisticsData.averageDurations.avgTechDurationSeconds =
        this.techDurationAvgs.reduce((a, b) => a + b, 0) / total;
      this.alertStatisticsData.averageDurations.avgQueueTimeSeconds =
        this.queueTimeAvgs.reduce((a, b) => a + b, 0) / total;
      this.alertStatisticsData.averageDurations.avgReservedTimeSeconds =
        this.reservedTimeAvgs.reduce((a, b) => a + b, 0) / total;
      this.alertStatisticsData.averageDurations.avgCareTimeSeconds =
        this.careTimeAvgs.reduce((a, b) => a + b, 0) / total;
    }

    this.techDurationAvgs = [];
    this.queueTimeAvgs = [];
    this.reservedTimeAvgs = [];
    this.careTimeAvgs = [];

    this.statistics.alertStatistics = structuredClone(this.alertStatisticsData);
    this.statistics.alertsTabPagingComplete = true;
    this.statistics.initPageCounter();
    this.totalAlerts = 0;
  }

  async collectExportSnapshots(
    onProgress?: StatisticsExportProgressCallback
  ): Promise<StatisticsExportSnapshot[]> {
    const widgetEntries: Array<{ widget: StatisticsExportWidget | undefined; titleKey: string }> = [
      { widget: this.alertsByLocation, titleKey: 'ALERTS_WITH_FIRST_LETTER_UPPERCASE' },
      { widget: this.alertsPerAlertType, titleKey: 'ALERTS_PER_ALERT_TYPE' },
      { widget: this.alertsPerDay, titleKey: 'ALERTS_PER_DAY' },
      { widget: this.alertsPerHour, titleKey: 'ALERTS_PER_HOUR' },
      { widget: this.averageAlertDurations, titleKey: 'AVERAGE_ALERT_DURATIONS' },
      { widget: this.alertsPerCareRecipient, titleKey: 'ALERTS_PER_CARE_RECIPIENT' },
    ];
    const snapshots: StatisticsExportSnapshot[] = [];
    const total = widgetEntries.length;

    for (let index = 0; index < widgetEntries.length; index++) {
      const { widget, titleKey } = widgetEntries[index];
      if (!widget) {
        throw new Error(
          `PDF export failed: widget "${titleKey}" is not available (view not rendered yet)`
        );
      }
      onProgress?.({
        phase: 'capture',
        current: index + 1,
        total,
        messageKey: titleKey,
      });
      try {
        const snapshot = await widget.captureForExport();
        // A widget with no data for the selected range returns null — skip it
        // instead of failing the whole export.
        if (snapshot) {
          snapshots.push(snapshot);
        }
      } catch (err) {
        throw new Error(`PDF export failed while capturing "${titleKey}": ${err instanceof Error ? err.message : String(err)}`);
      }
    }
    return snapshots;
  }

  getExportKpis(): StatisticsKpi[] {
    if (!this.alertStatisticsData) {
      return [];
    }
    const d = this.alertStatisticsData.averageDurations;
    return [
      {
        labelKey: 'ALERTS_WITH_FIRST_LETTER_UPPERCASE',
        value: String(this.alertStatisticsData.locationAlerts.totalAlerts),
      },
      {
        labelKey: 'TECHNICAL_DURATION',
        value: this.helper.convertSecondsToReadableFormat(d.avgTechDurationSeconds),
      },
      {
        labelKey: 'QUEUE_TIME',
        value: this.helper.convertSecondsToReadableFormat(d.avgQueueTimeSeconds),
      },
      {
        labelKey: 'RESERVED_TIME',
        value: this.helper.convertSecondsToReadableFormat(d.avgReservedTimeSeconds),
      },
      {
        labelKey: 'CARE_TIME',
        value: this.helper.convertSecondsToReadableFormat(d.avgCareTimeSeconds),
      },
    ];
  }

  ngOnDestroy(): void {
    this.subscription?.unsubscribe();
  }
}
